import type { ProjectDetailsProject, ProjectDetailsScene, ProjectDetailsStoryboardShot } from "@/types/project";

const MAX_ANCHOR_WORDS = 12;

export interface ScriptScrollAnchor {
  readonly startAnchorText: string;
  readonly endAnchorText: string | null;
  readonly shotFraction: number;
}

export function scriptSceneAnchorText(scriptExtract: string | null | undefined): string | null {
  const words = (scriptExtract ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, MAX_ANCHOR_WORDS);

  return words.length > 0 ? words.join(" ") : null;
}

export function shotFractionWithinScene(shots: readonly ProjectDetailsStoryboardShot[], activeShot: ProjectDetailsStoryboardShot): number {
  const sceneShots = shots.filter((s) => s["scene-id"] === activeShot["scene-id"]);
  if (sceneShots.length <= 1) {
    return 0;
  }

  const ordinal = sceneShots.findIndex((s) => s.id === activeShot.id);
  return ordinal <= 0 ? 0 : ordinal / sceneShots.length;
}

export function resolveScriptScrollAnchor(
  project: ProjectDetailsProject | null | undefined,
  activeShot: ProjectDetailsStoryboardShot | null | undefined
): ScriptScrollAnchor | null {
  if (!project || !activeShot || !activeShot["scene-id"]) {
    return null;
  }

  const scenes: readonly ProjectDetailsScene[] = project.scenes ?? [];
  const sceneIndex = scenes.findIndex((s) => s["scene-id"] === activeShot["scene-id"]);
  const scene = sceneIndex === -1 ? undefined : scenes[sceneIndex];
  if (!scene) {
    return null;
  }

  const startAnchorText = scriptSceneAnchorText(scene["script-extract"]);
  if (!startAnchorText) {
    return null;
  }

  const endAnchorText = scriptSceneAnchorText(scenes[sceneIndex + 1]?.["script-extract"]);
  const shotFraction = shotFractionWithinScene(project["storyboard-shots"] ?? [], activeShot);

  return { startAnchorText, endAnchorText, shotFraction };
}

export function scriptScrollAnchorKey(anchor: ScriptScrollAnchor | null): string {
  if (!anchor) {
    return "";
  }

  return `${anchor.startAnchorText}\u0000${anchor.endAnchorText ?? ""}\u0000${anchor.shotFraction}`;
}
